import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Avatar,
  Snackbar,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { getDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useLocation, Link } from "react-router-dom";
import {
  AccountBalanceWallet,
  MonetizationOn,
  LocalAtm,
  FormatListBulleted,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const drawerWidth = 240;

const Fullview = () => {
  const [data, setData] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("uid");
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (!data.userId) {
        setAlertSeverity("error");
        setAlertMessage("User ID is required to save data.");
        setShowAlert(true);
        return;
      }
      await setDoc(doc(db, "users", data.userId), {
        ...data,
        timeStamp: serverTimestamp(),
      });
      setAlertSeverity("success");
      setAlertMessage("Data saved successfully!");
    } catch (err) {
      console.error("Error saving data:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const formatTimestamp = (timestamp) => {
    const date = timestamp.toDate();
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    };
    return date.toLocaleDateString("en-US", options);
  };

  const drawerContent = (
    <div>
      <Box sx={{ height: "60px" }}></Box>
      <List>
        <ListItem button component={Link} to={`/myrequest?uid=${data.userId}`}>
          <ListItemIcon>
            <FormatListBulleted />
          </ListItemIcon>
          <ListItemText primary="MY Request" />
        </ListItem>
        <ListItem button component={Link} to={`/mytransactions?uid=${data.userId}`}>
          <ListItemIcon>
            <MonetizationOn />
          </ListItemIcon>
          <ListItemText primary="Transaction" />
        </ListItem>
       
      </List>
    </div>
  );

  return (
    <div className="App">
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #e0e0e0", // Bottom border
          boxShadow: "none", // Remove shadow
        }}
      >
        <Toolbar>
          {!isDesktop && (
            <IconButton
              edge="start"
              color="#292929"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="body" sx={{ flexGrow: 1, color: "#1e1e1e" }}>
            USER DETAILS
          </Typography>
          <Button color="inherit">Logout</Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? "permanent" : "temporary"}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>
      <main
        style={{ marginLeft: isDesktop ? drawerWidth : 0, padding: "20px 5px" }}
      >
        <Toolbar />
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6}>
            <Avatar
              alt={data.name}
              src={data.photoUrl || "/placeholder.jpg"}
              style={{
                width: 100,
                height: 100,
                margin: "0 auto",
                marginBottom: 20,
              }}
            />
            <Card style={{ margin: "0 auto" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  color="#272727"
                  align="center"
                  gutterBottom
                >
                  User Details View
                </Typography>
                <form onSubmit={handleSave}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Name"
                        name="name"
                        value={data.name || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                      <TextField
                        label="Email"
                        name="email"
                        value={data.email || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Phone"
                        name="phone"
                        value={data.phone || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Created At"
                        name="createdAt"
                        value={
                          data.createdAt ? formatTimestamp(data.createdAt) : ""
                        }
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="User ID"
                        name="userId"
                        value={data.userId || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="User Name"
                        name="userName"
                        value={data.userName || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Account No"
                        name="accountNo"
                        value={data.accountNo || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                      />
                      <TextField
                        label="Bank Name"
                        name="bankName"
                        value={data.bankName || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="IFSC Code"
                        name="ifscCode"
                        value={data.ifscCode || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="UPI ID"
                        name="upiId"
                        value={data.upiId || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Referral Code"
                        name="referralCode"
                        value={data.referralCode || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                        style={{ marginTop: "16px" }}
                      />
                    </Grid>

                    <Grid container justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                          m: 5,
                          backgroundColor: "#4952ff",
                          borderRadius: 15, // Setting border radius to 0
                          boxShadow: "none", // Removing elevation (shadow)
                          width: "200px", // Setting width to 200px
                        }}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
            <Snackbar
              open={showAlert}
              autoHideDuration={6000}
              onClose={handleAlertClose}
            >
              <Alert
                onClose={handleAlertClose}
                severity={alertSeverity}
                sx={{ width: "100%" }}
              >
                {alertMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </main>
    </div>
  );
};

export default Fullview;
